import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { RiSearch2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { attendanceReportRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAllLecturesRequest } from "../../../modules/admin-modules/lectures/Actions";
import {
  getManualExamsMarksRequest,
  getManualExamsRequest,
  setManualExamMarkRequest,
} from "../../../modules/admin-modules/exams/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const AttendanceSimpleReport = () => {
  const dispatch = useDispatch();
  const [lecId, setLecId] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [examId, setExamId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [scores, setScores] = useState({});
  const [sentRows, setSentRows] = useState({});

  const lectures = useSelector((state) => state.lectures.lectures);
  const exams = useSelector((state) => state.exams.exams);
  const examMarks = useSelector((state) => state.exams.marks);
  const attendancesReport = useSelector((state) => state.reports.attendancesReport);
  const reportLoading = useSelector((state) => state.reports.isLoading);
  const lecturesLoading = useSelector((state) => state.lectures.isLoading);

  useEffect(() => {
    dispatch(getAllLecturesRequest());
    dispatch(getManualExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!lecId) return;
    dispatch(attendanceReportRequest(lecId, date));
  }, [lecId, date, dispatch]);

  const lecOptions = useMemo(() => {
    const raw = Array.isArray(lectures) ? lectures : [];
    return raw.map((lec) => ({
      value: String(lec.id),
      label: lec.title || `Center #${lec.id}`,
    }));
  }, [lectures]);

  const examOptions = useMemo(() => {
    const raw = Array.isArray(exams) ? exams : [];
    return raw.map((exam) => ({
      value: String(exam.id),
      label: exam.name || `Exam #${exam.id}`,
    }));
  }, [exams]);

  const marksByStudent = useMemo(() => {
    const map = {};
    (Array.isArray(examMarks) ? examMarks : []).forEach((mark) => {
      if (mark?.student_id != null) map[mark.student_id] = mark;
    });
    return map;
  }, [examMarks]);

  const loadExamMarks = (id) => {
    setExamId(id);
    setScores({});
    setSentRows({});
    if (id) dispatch(getManualExamsMarksRequest(id));
  };

  const sendGrade = (studentId) => {
    const score = scores[studentId];
    if (score == null || score === "" || !examId) return;
    dispatch(
      setManualExamMarkRequest({
        student_id: studentId,
        exam_id: examId,
        score,
      })
    );
    setSentRows((prev) => ({ ...prev, [studentId]: true }));
  };

  const rows = useMemo(() => {
    const raw = Array.isArray(attendancesReport) ? attendancesReport : [];
    const term = searchTerm.trim().toLowerCase();
    return raw
      .filter((student) => {
        if (!term) return true;
        return (
          student?.name?.toLowerCase().includes(term) ||
          student?.phone?.toLowerCase().includes(term) ||
          student?.user_name?.toLowerCase().includes(term)
        );
      })
      .map((student, index) => {
        const studentMarks = marksByStudent[student.id];
        const hasScore =
          scores[student.id] != null && scores[student.id] !== ""
            ? true
            : Boolean(studentMarks?.score);
        return {
          key: student.id,
          index: index + 1,
          id: student.id,
          name: student.name ?? "—",
          code: student.user_name ?? "—",
          phone: student.phone ?? "—",
          defaultScore: studentMarks?.score ?? "",
          hasScore,
        };
      });
  }, [attendancesReport, searchTerm, marksByStudent, scores]);

  const showGradeColumns = Boolean(examId) && rows.length > 0;

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Center exam grading
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Load attendance for a center session, then pick a manual exam to enter or update
              student scores.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex
          gap={4}
          mb={6}
          flexWrap="wrap"
          align={{ base: "stretch", md: "flex-end" }}
        >
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Center
            </Text>
            <ReactSelect
              options={lecOptions}
              isLoading={lecturesLoading}
              placeholder="Select center"
              styles={selectStyles}
              value={lecOptions.find((o) => o.value === String(lecId)) || null}
              onChange={(opt) => setLecId(opt?.value ? String(opt.value) : "")}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>

          <Box minW={{ base: "100%", md: "200px" }} maxW={{ md: "240px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Date
            </Text>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(dayjs(e.target.value).format("YYYY-MM-DD"))}
              borderRadius="14px"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              fontWeight="600"
              _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
            />
          </Box>

          {rows.length > 0 ? (
            <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
              <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
                Manual exam
              </Text>
              <ReactSelect
                options={examOptions}
                placeholder="Select exam"
                styles={selectStyles}
                value={examOptions.find((o) => o.value === String(examId)) || null}
                onChange={(opt) => loadExamMarks(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
          ) : null}

          <Box flex="1" minW={{ base: "100%", md: "260px" }} maxW={{ md: "360px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <RiSearch2Fill color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                placeholder="Name, code, or phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="14px"
                borderColor="var(--border-subtle)"
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                fontWeight="600"
                _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
              />
            </InputGroup>
          </Box>
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
          overflowX="auto"
          color="var(--text-primary)"
        >
          {!lecId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a center and date to load students.
            </Text>
          ) : reportLoading && rows.length === 0 ? (
            <Text fontWeight="600" color="var(--text-primary)">
              Loading attendance…
            </Text>
          ) : rows.length === 0 ? (
            <Text fontWeight="600" color="var(--text-muted)">
              No students found for this session.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color="var(--text-primary)">#</Th>
                  <Th color="var(--text-primary)">Student</Th>
                  <Th color="var(--text-primary)">Code</Th>
                  <Th color="var(--text-primary)">Phone</Th>
                  {showGradeColumns ? (
                    <>
                      <Th color="var(--text-primary)">Score</Th>
                      <Th color="var(--text-primary)">Save</Th>
                    </>
                  ) : null}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => (
                  <Tr key={row.key}>
                    <Td fontWeight="700">#{row.index}</Td>
                    <Td fontWeight="600">{row.name}</Td>
                    <Td>{row.code}</Td>
                    <Td>{row.phone}</Td>
                    {showGradeColumns ? (
                      <>
                        <Td minW="120px">
                          <Input
                            size="sm"
                            placeholder="Score"
                            defaultValue={row.defaultScore}
                            onChange={(e) => {
                              setScores((prev) => ({
                                ...prev,
                                [row.id]: e.target.value,
                              }));
                              setSentRows((prev) => {
                                const next = { ...prev };
                                delete next[row.id];
                                return next;
                              });
                            }}
                            borderRadius="12px"
                            borderColor="var(--border-subtle)"
                            bg={
                              row.hasScore
                                ? "rgba(34, 84, 61, 0.12)"
                                : "rgba(155, 44, 44, 0.1)"
                            }
                            color="var(--text-primary)"
                            fontWeight="700"
                            _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
                          />
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            borderRadius="lg"
                            bg={sentRows[row.id] ? "green.600" : brandPalette.secondary}
                            color="white"
                            fontWeight="800"
                            minW="100px"
                            _hover={{
                              bg: sentRows[row.id] ? "green.700" : brandPalette.primary,
                            }}
                            onClick={() => sendGrade(row.id)}
                          >
                            {sentRows[row.id] ? "Saved" : "Save score"}
                          </Button>
                        </Td>
                      </>
                    ) : null}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {lecId && rows.length > 0 && !examId ? (
            <Text mt={4} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Select a manual exam above to enter scores.
            </Text>
          ) : null}
        </Box>
      </Box>
    </Layout>
  );
};

export default AttendanceSimpleReport;
